import { useState, useEffect, useMemo } from "react";
import {
    TextField,
    Autocomplete,
    CircularProgress,
    styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    AdministrativeArea,
    Country,
    fetchCountryById,
} from "../redux/countryActions";

export interface AdmnistrativeAreaSelectProps<T> {
    country: Country | null;
    value: T | null;
    errorMessage?: string;
    label?: string;
    containerSx?: any;
    sx?: any;
    multiple?: boolean;
    name?: string;
    onChange: (value: T | null) => void;
}

interface AdministrativeAreaOption {
    id: number;
    label: string;
    data: AdministrativeArea;
}

const AdmnistrativeAreaSelect = <
    T extends AdministrativeArea | AdministrativeArea[]
>({
    country,
    value,
    errorMessage,
    label,
    containerSx,
    sx,
    multiple = false,
    name = "administrativeArea",
    onChange,
}: AdmnistrativeAreaSelectProps<T>) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState<AdministrativeAreaOption[]>([]);
    const [loading, setLoading] = useState(false);

    const { t } = useTranslation();

    const getOptions = async (countryId?: number) => {
        if (!countryId) return;

        try {
            setLoading(true);
            const countryDetails = await fetchCountryById(countryId);

            if (!countryDetails?.administrativeAreas) return [];

            setOptions(
                countryDetails.administrativeAreas.map((area) => ({
                    id: area.id,
                    label: area.name,
                    data: area,
                }))
            );
        } catch (error) {
            console.error("Error fetching administrative areas:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getOptions(country?.id);
    }, [country]);

    const noOptionsText = useMemo(() => {
        if (!country) return t("inputPlaceholders.selectCountry");

        if (inputValue) return t("inputPlaceholders.noResultFound");

        return t("inputPlaceholders.search");
    }, [country, inputValue, t]);

    const handleChange = (
        newValue: AdministrativeAreaOption | AdministrativeAreaOption[] | null
    ) => {
        if (Array.isArray(newValue)) {
            onChange(
                newValue.map((area) => area.data as AdministrativeArea) as T
            );
            return;
        }

        onChange(newValue ? (newValue.data as AdministrativeArea as T) : null);
    };

    return (
        <Autocomplete
            value={
                Array.isArray(value)
                    ? options.filter((option) =>
                          value.find(({ id }) => option.id === id)
                      )
                    : options.find((option) => option.id === value?.id) ?? null
            }
            onChange={(_, newValue) => handleChange(newValue)}
            onInputChange={(_, newValue) => setInputValue(newValue)}
            inputValue={inputValue}
            options={options}
            loading={loading}
            noOptionsText={noOptionsText}
            loadingText={t("inputPlaceholders.loading")}
            sx={containerSx}
            multiple={multiple}
            renderInput={(params) => (
                <Input
                    {...params}
                    sx={sx}
                    label={
                        label ??
                        country?.administrativeAreaName ??
                        t("inputLabels.administrativeArea")
                    }
                    error={!!errorMessage}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    helperText={errorMessage}
                    value={inputValue}
                    name={name}
                />
            )}
        />
    );
};

export default AdmnistrativeAreaSelect;

const Input = styled(TextField)({
    backgroundColor: "white",
    borderRadius: "5px",
});
